import {useEffect} from 'react'
import {useSnackbar} from 'notistack5'

/**
 * Hook to monitor network connectivity and show notifications
 * @param {Object} options - Configuration options
 * @param {number} options.checkInterval - How often to check connectivity (ms)
 * @param {boolean} options.enabled - Whether monitoring is enabled
 */
const useNetworkMonitor = ({checkInterval = 5000, enabled = true} = {}) => {
  const {enqueueSnackbar} = useSnackbar()

  useEffect(() => {
    if (!enabled) return

    const interval = setInterval(() => {
      if (!navigator.onLine) {
        enqueueSnackbar('You are offline. Please check your internet connection.', {
          variant: 'error',
          preventDuplicate: true, // Prevent spam notifications
        })
      }
    }, checkInterval)

    return () => clearInterval(interval)
  }, [enqueueSnackbar, checkInterval, enabled])

  // Also check connection quality if available
  useEffect(() => {
    if (!enabled) return

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection

    if (connection) {
      const {downlink} = connection
      if (downlink < 0.5) {
        enqueueSnackbar('Slow internet connection, please wait', {
          variant: 'info',
          preventDuplicate: true,
        })
      }
    }
  }, [enqueueSnackbar, enabled])

  return {
    isOnline: navigator.onLine,
    connection: navigator.connection || navigator.mozConnection || navigator.webkitConnection,
  }
}

export default useNetworkMonitor
